import { ref, computed, onMounted, watch } from "vue";
import { setCookie, getCookie } from "@/utilities/cookieUtils";

// Create a singleton instance that persists across component instances
const isEstimateMode = ref(false);
const STORAGE_KEY = "really_estimate_mode";
const COOKIE_EXPIRATION_DAYS = 7; // Cookie will last for 7 days

export function useOfferTerminology() {
  // Initialize from cookie storage on mount
  onMounted(() => {
    const storedValue = getCookie(STORAGE_KEY);
    if (storedValue !== null) {
      isEstimateMode.value = storedValue === "true";
    }
  });

  // Persist changes to cookie storage
  watch(isEstimateMode, (newValue) => {
    setCookie(STORAGE_KEY, newValue.toString(), COOKIE_EXPIRATION_DAYS);
  });

  const setEstimateMode = (value: boolean) => {
    if (isEstimateMode.value !== value) {
      isEstimateMode.value = value;
      setCookie(STORAGE_KEY, value.toString(), COOKIE_EXPIRATION_DAYS);
    }
  };

  const offerTerm = computed(() =>
    isEstimateMode.value ? "Estimat" : "tilbud",
  );

  const offerTermCapitalized = computed(() =>
    isEstimateMode.value ? "Estimat" : "Tilbud",
  );

  const serviceOfferTerm = computed(() =>
    isEstimateMode.value ? "estimat for tjenester" : "tjenestetilbud",
  );

  const serviceOfferTermCapitalized = computed(() =>
    isEstimateMode.value ? "Estimat for tjenester" : "Tjenestetilbud",
  );

  const serviceOfferDescriptionTerm = computed(() =>
    isEstimateMode.value ? "Estimatbeskrivelse" : "Tjenestetilbudsbeskrivelse",
  );

  const offerStatusTerm = computed(() =>
    isEstimateMode.value ? "Estimatstatus" : "Tilbudsstatus",
  );

  const howToCreateButtonText = computed(
    () => `Hvordan lage ${offerTerm.value.toLowerCase()}?`,
  );

  const successMessages = computed(() => ({
    created: `${offerTermCapitalized.value} opprettet`,
    updated: `${offerTermCapitalized.value} oppdatert`,
    deleted: `${offerTermCapitalized.value} slettet`,
    // Add more message variations as needed
    nickname: `Kallenavn for ${offerTerm.value} oppdatert`,
  }));

  const errorMessages = computed(() => ({
    create: `Kunne ikke opprette ${offerTerm.value}`,
    update: `Kunne ikke oppdatere ${offerTerm.value}`,
    delete: `Kunne ikke slette ${offerTerm.value}`,
    fetch: `Kunne ikke hente ${offerTerm.value}`,
    // Add more error variations as needed
    nickname: `Kunne ikke oppdatere kallenavn for ${offerTerm.value}`,
  }));

  // Utility function to replace terminology in any text string
  const replaceTerminology = (text: string | undefined | null): string => {
    if (!text) return "";
    if (!isEstimateMode.value) return text;

    // First replace service offer terminology
    let result = text
      .replace(/Tilbudsstatus/g, offerStatusTerm.value)
      .replace(/tilbudsstatus/g, offerStatusTerm.value.toLowerCase())
      .replace(/Tjenestetilbudsbeskrivelse/g, serviceOfferDescriptionTerm.value)
      .replace(
        /tjenestetilbudsbeskrivelse/g,
        serviceOfferDescriptionTerm.value.toLowerCase(),
      )
      .replace(/Tjenestetilbud/g, serviceOfferTermCapitalized.value)
      .replace(/tjenestetilbud/g, serviceOfferTerm.value);

    // Then replace standard offer terminology
    result = result
      .replace(/TILBUD/g, "ESTIMAT")
      .replace(/Tilbudet/g, "Estimatet")
      .replace(/tilbudet/g, "estimatet")
      .replace(/Tilbud/g, "Estimat")
      .replace(/tilbud/g, "estimat");

    return result;
  };

  return {
    setEstimateMode,
    isEstimateMode,
    offerTerm,
    offerTermCapitalized,
    serviceOfferTerm,
    serviceOfferTermCapitalized,
    serviceOfferDescriptionTerm,
    offerStatusTerm,
    howToCreateButtonText,
    successMessages,
    errorMessages,
    replaceTerminology,
  };
}
